import { useEffect, useState } from 'react';
import Modal from 'components/Modal/Modal';
import { DATA_SET_PAGINATION_COUNT, INVESTORS_CUSTOMERS_GROUP_CELLS } from 'constants/dataSets';
import { useGetRefinementQuestionsQuery } from 'store/slices/materialAssessment/apis/materialAssessmentApi';

import RefinementQuestionsTable from './components/RefinementQuestionsTable/RefinementQuestionsTable';

import type IStakeholderGroupsQuestionsItem from 'store/slices/materialAssessment/interfaces/IStakeholderGroupsQuestionsItem';
import type IRefinementQuestionsModal from './interfaces/IRefinementQuestionsModal';

import styles from './scss/RefinementQuestionsModal.module.scss';

function RefinementQuestionsModal({
  open,
  title,
  onClose,
  currentGroupId,
  recordId,
}: IRefinementQuestionsModal) {
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState<IStakeholderGroupsQuestionsItem[]>([]);

  const { data: refinementQuestionsData, refetch } = useGetRefinementQuestionsQuery(
    {
      maId: recordId,
      offset,
      id: currentGroupId ?? 0,
    },
    {
      skip: currentGroupId === null || recordId === '' || !open,
      refetchOnMountOrArgChange: true,
    },
  );

  const hasMore = refinementQuestionsData?.data?.meta?.hasNext ?? false;

  const loadMore = () => {
    if (refinementQuestionsData?.data?.meta?.hasNext) {
      setOffset((prev) => prev + DATA_SET_PAGINATION_COUNT);
    }
  };

  useEffect(() => {
    if (!open) {
      setOffset(0);
      setItems([]);
    } else if (currentGroupId !== null && recordId) {
      refetch();
    }
  }, [open, currentGroupId, recordId, refetch]);

  useEffect(() => {
    if (currentGroupId === null || !recordId) {
      setOffset(0);
      setItems([]);
      return;
    }
    setOffset(0);
    setItems([]);
  }, [currentGroupId, recordId]);

  useEffect(() => {
    if (!refinementQuestionsData?.data?.items?.length) return;
    const newItems = refinementQuestionsData.data.items;
    setItems((prev) => {
      if (offset === 0) return newItems;
      const existingIds = new Set(prev.map((item) => item.id));
      const appended = newItems.filter((item) => !existingIds.has(item.id));
      return appended.length > 0 ? [...prev, ...appended] : prev;
    });
  }, [refinementQuestionsData, offset]);

  return (
    <Modal
      open={open}
      title={title || ''}
      onClose={onClose}
      className={styles.modalContainer}
      isButtonsVisible={false}
    >
      <div className={styles.root}>
        <RefinementQuestionsTable
          headCells={INVESTORS_CUSTOMERS_GROUP_CELLS}
          items={items}
          hasMore={hasMore}
          loadMore={loadMore}
        />
      </div>
    </Modal>
  );
}

export default RefinementQuestionsModal;
